import { useMemo } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import { screenPadding, spacing } from './layout';

/**
 * How the app fits itself to the device it is running on.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 * Every screen was drawn against one phone - a 390pt-wide, 844pt-tall handset
 * - and the numbers in the stylesheets are that phone's numbers. They survive
 * a 360pt Android and a 430pt Pro Max because nothing in the layout is far
 * from flexible. They do not survive the two ends:
 *
 *   - **320–360pt phones** (SE 1st gen, Galaxy A-series in display-zoom): a
 *     40pt hero headline wraps to three lines, the hero's fixed 276pt block
 *     takes half a 568pt screen, and a three-across grid leaves 88pt tiles
 *     carrying two-word labels.
 *   - **600pt+ tablets and unfolded foldables**: a rail card at 42% of the
 *     width is a 430pt card, a chat bubble at 78% is a 800pt line of text
 *     nobody can track back to the left margin, and a full-bleed sheet is a
 *     form with a metre of empty space either side of it.
 *
 * The fix is not "scale everything by width" - that turns a tablet into a
 * magnified phone, with 60pt icons and 70pt buttons. A control is sized by the
 * finger that presses it, and fingers are the same on every device. So:
 *
 *   - **Touch targets, icons, borders, radii stay fixed.** They are already
 *     right, and nothing here touches them.
 *   - **Content blocks flex** - column counts, card widths, artwork, line
 *     length. These are the things that are wrong at the ends.
 *   - **Type moves a little**, damped, never proportionally (see `typeScale`).
 *
 * ---------------------------------------------------------------------------
 * TWO WAYS IN
 * ---------------------------------------------------------------------------
 * `useResponsive()` for anything rendered - it re-runs on rotation, on a fold
 * opening, and on a split-screen resize, which `Dimensions.get()` at module
 * load does not.
 *
 * The pure helpers (`sizeClassOf`, `typeScale`, `scaleOn`) for the handful of
 * places that need a number before render - `StyleSheet.create` runs once at
 * import, and the type ramp is built there.
 */

/**
 * The device every screen in this app was drawn against: 390 × 844.
 *
 * Ratios below are relative to this, so a value of 1.0 means "exactly as it
 * was designed" - the reference phone renders byte-identically to before this
 * file existed, which is what makes the whole thing safe to add late.
 */
export const BASE_WIDTH = 390;
export const BASE_HEIGHT = 844;

/**
 * Where the layout changes its mind.
 *
 * `compact` at 360 rather than 375: 360 is the single most common Android
 * width in the world and it must NOT be treated as a cramped device - it is
 * the normal case. Below it (320–359) is where things genuinely stop fitting.
 *
 * `tablet` at 600 is the same line Android's own `sw600dp` draws, and it
 * catches an unfolded foldable and an iPad mini in portrait alike.
 */
export const breakpoints = {
  compact: 360,
  tablet: 600,
} as const;

/**
 * The widest a single column of text or rows is allowed to get.
 *
 * Past this a list row becomes a label at the far left and a price at the far
 * right with a metre of nothing between them - the eye cannot connect the two.
 * Every page pins its content to a column this wide and centres it, so a
 * tablet shows a comfortable page rather than a stretched phone.
 */
export const MAX_CONTENT_WIDTH = 560;

/**
 * The same ceiling for the WEB build's page column.
 *
 * A desktop browser window is the one place the 560 column reads as skinny -
 * a website's centre column runs wider than a reading column on a tablet.
 * 1280 is the ordinary website container width, and the layouts absorb it
 * the way they absorb a tablet: `columns()` turns the extra room into more
 * grid tiles (each still capped by its own `maxTile`), rails show more
 * cards (capped by `railCard`), and line lengths are capped per component.
 * Phones - native or mobile browser - never reach either ceiling, and
 * everything that must stay a readable form width (`sheetWidth`, the
 * pinned bars, the sheets' own `maxWidth`) still pins to
 * `MAX_CONTENT_WIDTH` above.
 */
const MAX_PAGE_WIDTH_WEB = 1280;

const maxContentWidth = (): number =>
  Platform.OS === 'web' ? MAX_PAGE_WIDTH_WEB : MAX_CONTENT_WIDTH;

/**
 * The reading column's own web ceiling.
 *
 * 560 is right for a handheld reading column, but centred in a desktop
 * window it leaves a canyon of margin either side and the app reads as a
 * phone floating in white space. 720 is the ordinary web list width - wide
 * enough to own the page, still narrow enough that a row's title and its
 * Add button stay in one eyeful. Native never sees this number.
 */
const READING_WIDTH_WEB = 720;

const maxReadingWidth = (): number =>
  Platform.OS === 'web' ? READING_WIDTH_WEB : MAX_CONTENT_WIDTH;

/**
 * Ceiling on the OS font-size setting, as a multiplier.
 *
 * Accessibility text scaling goes to 3.5× on iOS and 2× on Android. This app
 * is full of fixed-height controls (44/52pt buttons, 48pt input bars) and
 * two-up rows, and at 2× every one of them clips its own label. 1.3 is the
 * most the layouts absorb without anything being cut off, and it still honours
 * the first several steps of the setting, which is where nearly everyone who
 * changes it lands.
 *
 * Applied in `components/ui/Text.tsx`, so every string in the app inherits it.
 */
export const MAX_FONT_SCALE = 1.3;

/**
 * Under this many points tall, a screen is "short": a small phone, or any
 * phone on its side.
 *
 * Separate from the width breakpoints because the two fail differently - a
 * narrow screen wraps text, a short one pushes the button below the fold.
 * Exported so the handful of screens that lay themselves out vertically in
 * arithmetic rather than flexbox (splash, onboarding) test against the same
 * line `isShort` draws, instead of each copying the number.
 */
export const SHORT_HEIGHT = 700;

export type SizeClass = 'compact' | 'regular' | 'tablet';

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

/** Which of the three layouts a width belongs to. */
export const sizeClassOf = (width: number): SizeClass =>
  width >= breakpoints.tablet
    ? 'tablet'
    : width < breakpoints.compact
    ? 'compact'
    : 'regular';

/**
 * A damped scale against the reference width.
 *
 * `factor` is how much of the width difference is passed through: 0 keeps the
 * value fixed, 1 makes it fully proportional. The default 0.5 is the useful
 * middle - a 320pt phone gets 91% of a value and a 768pt tablet 148% of it
 * before clamping, which is enough to notice and not enough to distort.
 *
 * Clamped at both ends because proportional scaling on a 1024pt tablet is how
 * you get a 100pt avatar: past a point, more width should buy more *content*,
 * not bigger content.
 */
export const scaleOn = (
  width: number,
  size: number,
  factor = 0.5,
  { min = 0.88, max = 1.4 }: { min?: number; max?: number } = {},
) => Math.round(size * clamp(1 + (width / BASE_WIDTH - 1) * factor, min, max));

/**
 * Multiplier for the type ramp on a given width.
 *
 * Type is the one thing that must move gently. The sizes are a designed ramp
 * with hand-set line heights and letter-spacing, and scaling them
 * proportionally breaks the relationships between the steps - a 40pt display
 * over a 15pt body is a hierarchy; 52 over 20 is a poster.
 *
 * ---------------------------------------------------------------------------
 * BUT IT DOES HAVE TO MOVE
 * ---------------------------------------------------------------------------
 * This used to return exactly 1 for every width from 360 to 600, on the
 * reasoning that "nearly every phone" lives there and the design was signed
 * off at 390. That was wrong in a way that only shows on hardware: a 430pt Pro
 * Max was drawing the same 15pt body as a 390pt phone, on a display an inch
 * bigger. Nothing was broken and everything looked small - the screen had
 * grown and the app had not.
 *
 * So above the reference width it grows, damped to roughly half the width
 * difference and capped, which keeps the ramp's internal relationships intact
 * while letting a bigger phone feel like one:
 *
 *   390pt (reference)  1.00   unchanged, by definition
 *   412pt              1.03
 *   430pt              1.06
 *   480pt+             1.10   the cap
 *
 * Below 360 it steps down for the opposite reason. Between 360 and 390 it
 * stays at 1: that band is where the design was drawn and it needs no help.
 */
export const typeScale = (width: number): number => {
  if (width >= breakpoints.tablet) {
    /* Above the phone cap, never below it - a 600pt tablet stepping DOWN from
       a 580pt foldable would be a cliff in the wrong direction. */
    return 1.12;
  }

  if (width < breakpoints.compact) {
    // 320pt gets 0.9, 359pt gets ~0.97: proportional across the compact band
    // rather than a cliff, so a 355pt phone is not punished like a 320pt one.
    return clamp(0.9 + ((width - 320) / (breakpoints.compact - 320)) * 0.1, 0.9, 1);
  }

  /* Half the width difference from the reference, capped at a tenth. Damped
     rather than proportional: at 1:1 a 430pt phone would take the display to
     44pt and the body to 16.5, which is a different design. */
  return clamp(1 + (width / BASE_WIDTH - 1) * 0.55, 1, 1.1);
};

export type Responsive = {
  /** Live window size, in points. */
  width: number;
  height: number;
  /** The OS text-size setting, already clamped to `MAX_FONT_SCALE`. */
  fontScale: number;

  sizeClass: SizeClass;
  /** 320–359pt: small phones. Everything has to give a little. */
  isCompact: boolean;
  /** 600pt+: tablets, unfolded foldables, wide split-screen. */
  isTablet: boolean;
  isLandscape: boolean;
  /**
   * Under 700pt tall - a small phone, or any phone in landscape.
   *
   * Separate from `isCompact` because the two fail differently: a narrow
   * screen wraps text, a short one pushes the button below the fold. Anything
   * sized as a share of the height reads this.
   */
  isShort: boolean;

  /** Width of the centred content column: the window, capped. */
  contentWidth: number;
  /** Dead space each side of that column on a wide screen; 0 on a phone. */
  sideInset: number;
  /**
   * Padding that centres a page's content column on a wide screen, and is
   * exactly zero on every phone.
   *
   * Applied to a screen's outermost scroller, NOT to the rows inside it. The
   * gutter stays `screenPadding` everywhere - one inset for the whole app is
   * an invariant this codebase defends (see `theme/layout.ts`), and a page
   * that widened its own gutter on a tablet while the sheet over it did not
   * would look misaligned rather than roomy. All this does is narrow the page
   * the gutter is measured from.
   */
  column: { paddingHorizontal: number };
  /**
   * The same, plus the page gutter - for a container that sits outside the
   * scroller and so owns its own inset (a pinned footer bar, a rail's content
   * container).
   */
  page: { paddingHorizontal: number };

  /**
   * Dead space each side of the READING column, and the padding that pins
   * content to it - for rows, forms and prose, as `page` is for pages.
   *
   * The page column may legitimately run wide (1280 on desktop web - grids
   * and banners use every point of it), but a LIST row stretched across it
   * puts the label at one edge and the price at the other, which is the
   * exact failure `MAX_CONTENT_WIDTH` documents. Reading content pins to
   * that readable width whatever the page around it does. On native the
   * page column is already capped at the same number, so these equal
   * `sideInset`/`page` there and nothing changes.
   */
  readingInset: number;
  reading: { paddingHorizontal: number };

  /** Damped scale against the reference width - see `scaleOn`. */
  scale: (size: number, factor?: number) => number;
  /**
   * The same, against the reference *height*.
   *
   * For blocks whose job is to occupy a share of the page - a hero, a splash
   * lockup, a map. Scaling those off the width leaves them square-ish on a
   * tablet in landscape, where there is width to spare and no height at all.
   */
  vscale: (size: number, factor?: number) => number;

  /**
   * Width of one card in a horizontal rail.
   *
   * Takes the share of the screen the design asks for, then holds it between
   * a readable minimum and a maximum - past ~260pt a card in a rail stops
   * reading as "there is more to the right" and starts reading as a page.
   */
  railCard: (
    ratio: number,
    opts?: { min?: number; max?: number },
  ) => number;

  /**
   * Columns for a wrapping grid: as many as fit between `minTile` and
   * `maxTile`, never fewer than one and never more than `max`.
   *
   * This is the responsive move that actually matters on a tablet - the extra
   * width becomes more tiles rather than bigger ones.
   */
  columns: (opts: {
    preferred: number;
    minTile?: number;
    maxTile?: number;
    max?: number;
    gap?: number;
    available?: number;
  }) => number;

  /**
   * Width a modal sheet should take. Full-bleed on a phone; a centred panel on
   * a tablet, where an edge-to-edge sheet is a form with half a metre of dead
   * space in it.
   */
  sheetWidth: number;
};

/**
 * Live layout metrics. Re-renders on rotation, fold and split-screen resize.
 *
 * Everything is derived in one `useMemo` keyed on the three inputs, so a
 * component reading five of these values pays for one object, and the object
 * is referentially stable between unrelated re-renders - which matters because
 * several of these feed `React.memo`'d children.
 */
export function useResponsive(): Responsive {
  const { width, height, fontScale } = useWindowDimensions();

  return useMemo(() => {
    const sizeClass = sizeClassOf(width);
    const isCompact = sizeClass === 'compact';
    const isTablet = sizeClass === 'tablet';

    const contentWidth = Math.min(width, maxContentWidth());
    const sideInset = Math.round((width - contentWidth) / 2);

    const readingWidth = Math.min(width, maxReadingWidth());
    const readingInset = Math.round((width - readingWidth) / 2);

    return {
      width,
      height,
      fontScale: Math.min(fontScale, MAX_FONT_SCALE),

      sizeClass,
      isCompact,
      isTablet,
      isLandscape: width > height,
      isShort: height < SHORT_HEIGHT,

      contentWidth,
      sideInset,
      column: { paddingHorizontal: sideInset },
      page: { paddingHorizontal: screenPadding + sideInset },
      readingInset,
      reading: { paddingHorizontal: screenPadding + readingInset },

      scale: (size, factor) => scaleOn(width, size, factor),
      vscale: (size, factor = 0.5) =>
        Math.round(
          size *
            Math.min(1.35, Math.max(0.85, 1 + (height / BASE_HEIGHT - 1) * factor)),
        ),

      railCard: (ratio, { min = 150, max = 260 } = {}) =>
        Math.round(clamp(width * ratio, Math.min(min, width - 48), max)),

      columns: ({
        preferred,
        minTile = 88,
        maxTile = 160,
        max = 6,
        gap = spacing.sm,
        available = contentWidth - screenPadding * 2,
      }) => {
        const fits = (n: number) => (available - gap * (n - 1)) / n;

        let n = Math.max(1, Math.round(preferred));
        // Down first: an asked-for column count that leaves unreadable tiles is
        // a wish, not a promise.
        while (n > 1 && fits(n) < minTile) {
          n -= 1;
        }
        // Then up, but only while the tiles would otherwise be oversized. On a
        // phone this loop never runs - the tiles are already inside the band.
        while (n < max && fits(n + 1) >= minTile && fits(n) > maxTile) {
          n += 1;
        }
        return n;
      },

      sheetWidth: isTablet ? Math.min(width, MAX_CONTENT_WIDTH) : width,
    };
  }, [fontScale, height, width]);
}
